import { useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Flex,
  Icon,
  Image,
  Link,
  List,
  ListItem,
  PanelHeader,
  PanelSection,
  PanelTitle,
  Paragraph,
  PlainButton,
  Stack,
  Text,
  Tooltip,
  TooltipContent,
} from '@customerio/pluma-components/react';
import { isEmptyString } from '@/utils/string';
import defaultAccountLogo from '@/assets/images/default-account-logo.svg';
import { useLoginFlow } from '../login-flow';
import {
  formatChooseUserEntries,
  isOtherThanGoogleSso,
  sortChooseUserEntries,
  type FormattedChooseUserEntry,
} from '../utils/choose-user';
import { LoginAnalyticsEvent, trackLoginEvent } from '../utils/track-login-event';
import * as styles from '../index.css';

// Matches Ember's externalIdentityProviderLabel (used for the chooser row's
// "Sign in with …" label).
const PROVIDER_LABEL: Record<string, string> = {
  okta_oidc: 'Okta',
  azure_oidc: 'Azure Active Directory',
  generic_oidc: 'Generic OpenID Connect',
  generic_saml: 'SAML 2.0',
};

function providerLabel(provider: string): string {
  return PROVIDER_LABEL[provider] ?? 'SSO';
}

export function ChooseUserStep() {
  const { chooseUserEntries, googleAccountEmail, isSubmitting, chooseUser, resetToEmailStep } = useLoginFlow();

  const entries = useMemo(() => sortChooseUserEntries(formatChooseUserEntries(chooseUserEntries)), [chooseUserEntries]);

  // Fire once when the chooser is shown — mirrors the Ember choose-user route's
  // `activate()` analytics.
  const hasTrackedView = useRef(false);
  useEffect(() => {
    if (hasTrackedView.current) {
      return;
    }
    hasTrackedView.current = true;
    trackLoginEvent(LoginAnalyticsEvent.ChooseUserListViewed, { 'Users Count': entries.length });
  }, [entries.length]);

  return (
    <>
      <PanelHeader>
        <PanelTitle level="1" data-test-login-signup-title>
          Welcome back,
          <br />
          <Text data-test-login-form-email-title>{googleAccountEmail}</Text>!
        </PanelTitle>
      </PanelHeader>
      <PanelSection>
        <Paragraph mb="600">
          Not this account?{' '}
          <Link isUnderlined onClick={resetToEmailStep} data-test-login-form-use-different-email>
            Use a different email
          </Link>
        </Paragraph>

        <Paragraph mb="200">Choose which Customer.io account you want to sign into.</Paragraph>

        <List styleType="none" className={styles.chooseUserList} data-test-login-form-choose-user>
          {entries.map((entry) => (
            <ListItem key={`${entry.userID}:${entry.userEmail}`} className={styles.chooseUserListItem}>
              <ChooseUserItem
                entry={entry}
                // Lock the whole list while one selection is in flight so a
                // second click can't kick off a parallel SSO popup / authenticate.
                isSubmitting={isSubmitting}
                onChoose={() => void chooseUser(entry)}
                onUseDifferentEmail={resetToEmailStep}
              />
            </ListItem>
          ))}
        </List>
      </PanelSection>
    </>
  );
}

type ChooseUserItemProps = {
  entry: FormattedChooseUserEntry;
  isSubmitting?: boolean;
  onChoose: () => void;
  onUseDifferentEmail: () => void;
};

function ChooseUserItem({ entry, isSubmitting, onChoose, onUseDifferentEmail }: ChooseUserItemProps) {
  const logo = !isEmptyString(entry.accountLogo) ? entry.accountLogo : defaultAccountLogo;
  const showSsoLabel = isOtherThanGoogleSso(entry.accountSSOProvider);
  const mismatch = entry.hasGoogleSSODomainMismatch;

  return (
    // Domain-mismatched rows are non-actionable; a custom tooltip explains why
    // and offers a way out (matches the Ember `PlumaTooltip` + `TooltipContent`).
    <Tooltip placement="bottom" isCustom isDisabled={!mismatch}>
      {(context) => (
        <>
          <PlainButton
            ref={context.reference.setReference}
            {...context.reference.getReferenceProps()}
            className={styles.chooseUserItem}
            // Only the submit-lock natively disables the button. Mismatch rows
            // use `aria-disabled` instead — a natively-disabled button swallows
            // the hover events the tooltip needs (which is why Ember had to put
            // the tooltip on a nested div). `chooseUser` already no-ops a
            // mismatch click, so the button can own the tooltip directly.
            isDisabled={isSubmitting === true}
            aria-disabled={mismatch || isSubmitting === true ? true : undefined}
            onClick={onChoose}
            aria-label={`Sign in to ${entry.accountName} as ${entry.userEmail}`}
            data-test-login-form-choose-user-item
          >
            <Flex alignItems="center" justifyContent="space-between" w="full" gap="150">
              <Flex alignItems="center" gap="150">
                <Image src={logo} alt="" className={styles.chooseUserLogo} />
                <Stack gap="0">
                  <Text size="m" weight="bold" data-test-login-form-choose-user-name>
                    {entry.accountName}
                  </Text>
                  <Box data-test-login-form-choose-user-user-email>{entry.userEmail}</Box>
                </Stack>
              </Flex>
              <Flex justifyContent="flex-end" alignItems="center" gap="100" data-test-login-form-choose-user-action>
                {showSsoLabel ? (
                  <Link variant="secondary" isUnderlined>
                    Sign in with {providerLabel(entry.accountSSOProvider)}
                  </Link>
                ) : null}
                <Icon name="chevron-right" />
              </Flex>
            </Flex>
          </PlainButton>

          {mismatch ? (
            <TooltipContent>
              <Paragraph mb="200" color="inherit">
                This account has Google SSO enabled and requires all team member to log in with [@
                {entry.accountSSOOIDCGoogleHostedDomain}] email. An admin on your account can verify or update your
                email in Customer.io.
              </Paragraph>
              <Link variant="secondary" isUnderlined onClick={onUseDifferentEmail}>
                Sign in with a different email
              </Link>
            </TooltipContent>
          ) : null}
        </>
      )}
    </Tooltip>
  );
}
